import { ArrowTopRightOnSquareIcon, ChevronDownIcon, CloudArrowUpIcon, TrashIcon } from "@heroicons/react/24/outline";
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "../../../utils/toast";
import { uploadMediaFile } from "../../../services/api";
import { FormErrors, SponsorItem, UpdateProfileUIProps } from "../../../types/updateProfileProps";
import {
    AddPill,
    Card,
    FieldError,
    FormActions,
    GhostButton,
    SectionTitle,
    TextAreaField,
    TextField,
    getDateLanguage,
    isHttpUrl,
} from "./formPrimitives";

const LOGO_TYPES = ["image/png", "image/jpeg"];
const LOGO_MAX_BYTES = 2 * 1024 * 1024;
const NAME_MAX_LENGTH = 50;
/** Backend limit is 500 characters (Figma shows 5000) – see BACKEND_CHANGES.md #4 */
const DESCRIPTION_MAX_LENGTH = 500;
const LINK_MAX_LENGTH = 5000;

const SponsorLogo: React.FC<{ logo: string; name: string }> = ({ logo, name }) => {
    if (logo) {
        return <img src={logo} alt={name} className="h-16 w-16 shrink-0 object-contain" />;
    }

    return (
        <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-xl bg-[#EBEBEC] text-xl font-bold text-gray-900">
            {name.charAt(0).toUpperCase()}
        </div>
    );
};

const SponsorshipsSection: React.FC<UpdateProfileUIProps> = ({ sponsors, commitSponsors, isPersisting }) => {
    const { t, i18n } = useTranslation("updateProfile");

    const [openIndex, setOpenIndex] = useState<number | null>(null);
    const [isAdding, setIsAdding] = useState(false);
    const [isUploading, setIsUploading] = useState(false);

    const [name, setName] = useState("");
    const [description, setDescription] = useState("");
    const [link, setLink] = useState("");
    const [logoFile, setLogoFile] = useState<File | null>(null);
    const [previewUrl, setPreviewUrl] = useState("");
    const [errors, setErrors] = useState<FormErrors>({});

    const fileInputRef = useRef<HTMLInputElement>(null);
    const isBusy = isPersisting || isUploading;

    // frees the temporary preview whenever it is replaced and when the section unmounts
    useEffect(() => {
        return () => {
            if (previewUrl) URL.revokeObjectURL(previewUrl);
        };
    }, [previewUrl]);

    const clearError = (key: string) => {
        setErrors((previous) => ({ ...previous, [key]: "" }));
    };

    const closeForm = () => {
        setIsAdding(false);
        setName("");
        setDescription("");
        setLink("");
        setLogoFile(null);
        setPreviewUrl("");
        setErrors({});
    };

    const pickLogo = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        event.target.value = "";
        if (!file) return;

        if (!LOGO_TYPES.includes(file.type)) {
            setErrors((previous) => ({ ...previous, logo: t("page.errors.logoType") }));
            return;
        }
        if (file.size > LOGO_MAX_BYTES) {
            setErrors((previous) => ({ ...previous, logo: t("page.errors.logoSize") }));
            return;
        }

        setLogoFile(file);
        setPreviewUrl(URL.createObjectURL(file));
        clearError("logo");
    };

    const validate = (): FormErrors => {
        const formErrors: FormErrors = {};

        if (!name.trim()) formErrors.name = t("page.errors.required");
        if (description.length > DESCRIPTION_MAX_LENGTH) {
            formErrors.description = t("page.errors.maxLength", { max: DESCRIPTION_MAX_LENGTH });
        }

        const trimmedLink = link.trim();
        if (trimmedLink && !isHttpUrl(trimmedLink)) {
            formErrors.link = t("page.errors.invalidUrl");
        } else if (trimmedLink.length > LINK_MAX_LENGTH) {
            formErrors.link = t("page.errors.maxLength", { max: LINK_MAX_LENGTH });
        }

        return formErrors;
    };

    const addSponsor = async () => {
        const formErrors = validate();
        setErrors(formErrors);
        if (Object.keys(formErrors).length > 0) return;

        setIsUploading(true);
        try {
            const logo = logoFile ? await uploadMediaFile(logoFile) : "";
            const sponsor: SponsorItem = {
                name: name.trim(),
                description: description.trim(),
                logo,
                link: link.trim(),
            };

            const isSaved = await commitSponsors([...sponsors, sponsor]);
            if (isSaved) closeForm();
        } catch {
            toast.error(t("page.errors.imageUpload"));
        } finally {
            setIsUploading(false);
        }
    };

    const removeSponsor = async (index: number) => {
        const isSaved = await commitSponsors(sponsors.filter((_, itemIndex) => itemIndex !== index));
        if (isSaved) setOpenIndex(null);
    };

    const toggleSponsor = (index: number) => {
        setOpenIndex(openIndex === index ? null : index);
    };

    /** "28 September 2026" – shown only when the backend sends a createdAt date */
    const formatAddedDate = (createdAt?: string | null) => {
        if (!createdAt) return "";

        const date = new Date(createdAt);
        if (Number.isNaN(date.getTime())) return "";

        const language = getDateLanguage(i18n.language);
        const locale = language === "en" ? "en-GB" : language;
        return date.toLocaleDateString(locale, { day: "numeric", month: "long", year: "numeric" });
    };

    return (
        <section id="section-sponsors" className="flex flex-col gap-4">
            <SectionTitle>{t("page.sponsors")}</SectionTitle>

            {!isAdding && (
                <AddPill onClick={() => setIsAdding(true)} disabled={isBusy}>
                    {t("page.addSponsors")}
                </AddPill>
            )}

            {sponsors.map((sponsor, index) => {
                const isOpen = openIndex === index;
                const addedDate = formatAddedDate(sponsor.createdAt);

                return (
                    <Card key={`${index}-${sponsor.name}`} className="flex flex-col gap-4">
                        <button
                            type="button"
                            aria-expanded={isOpen}
                            onClick={() => toggleSponsor(index)}
                            className="flex w-full items-center gap-4 text-left"
                        >
                            <SponsorLogo logo={sponsor.logo} name={sponsor.name} />
                            <span className="min-w-0 flex-1 break-words text-base font-bold leading-6 text-gray-900">
                                {sponsor.name}
                            </span>
                            <ChevronDownIcon
                                className={`h-5 w-5 shrink-0 text-gray-900 transition-transform ${
                                    isOpen ? "rotate-180" : ""
                                }`}
                            />
                        </button>

                        {isOpen && (
                            <div className="flex flex-col gap-4">
                                {sponsor.description && (
                                    <p className="whitespace-pre-line break-words text-base leading-6 text-gray-900">
                                        {sponsor.description}
                                    </p>
                                )}

                                {sponsor.link && isHttpUrl(sponsor.link) && (
                                    <a
                                        href={sponsor.link}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="inline-flex w-fit max-w-full items-center gap-1 break-all text-sm text-[#0485F7] hover:underline"
                                    >
                                        {sponsor.link}
                                        <ArrowTopRightOnSquareIcon className="h-3 w-3 shrink-0" />
                                    </a>
                                )}

                                <div className="flex items-center justify-between gap-3">
                                    <span className="text-sm text-gray-500">
                                        {addedDate && t("page.addedOn", { date: addedDate })}
                                    </span>
                                    <GhostButton disabled={isBusy} onClick={() => removeSponsor(index)}>
                                        <TrashIcon className="h-4 w-4" />
                                        {t("page.remove")}
                                    </GhostButton>
                                </div>
                            </div>
                        )}
                    </Card>
                );
            })}

            {isAdding && (
                <Card className="flex flex-col gap-3">
                    <div className="flex flex-col gap-1">
                        <input
                            ref={fileInputRef}
                            type="file"
                            accept="image/png,image/jpeg"
                            onChange={pickLogo}
                            className="hidden"
                        />
                        <button
                            type="button"
                            disabled={isBusy}
                            onClick={() => fileInputRef.current?.click()}
                            className="flex w-full flex-col items-center justify-center gap-1 rounded-2xl border border-dashed border-gray-300 px-4 py-6 text-center transition-colors hover:bg-gray-50 disabled:opacity-50"
                        >
                            {previewUrl ? (
                                <img src={previewUrl} alt="" className="h-16 w-16 object-contain" />
                            ) : (
                                <CloudArrowUpIcon className="h-8 w-8 text-gray-500" />
                            )}
                            <span className="text-sm text-gray-900">{t("page.uploadImage")}</span>
                            <span className="text-xs text-gray-500">{t("page.uploadHint")}</span>
                        </button>
                        <FieldError message={errors.logo} />
                    </div>

                    <TextField
                        name="sponsorName"
                        label={t("page.sponsorName")}
                        required
                        value={name}
                        maxLength={NAME_MAX_LENGTH}
                        placeholder={t("page.enterSponsorName")}
                        onValueChange={(value) => {
                            setName(value);
                            clearError("name");
                        }}
                        error={errors.name}
                    />
                    <TextAreaField
                        name="sponsorDescription"
                        label={t("page.sponsorDescription")}
                        value={description}
                        maxLength={DESCRIPTION_MAX_LENGTH}
                        placeholder={t("page.writeHere")}
                        counterText={t("page.characters", {
                            count: description.length,
                            max: DESCRIPTION_MAX_LENGTH,
                        })}
                        onValueChange={(value) => {
                            setDescription(value);
                            clearError("description");
                        }}
                        error={errors.description}
                    />
                    <TextField
                        name="sponsorLink"
                        label={t("page.companyWebsite")}
                        value={link}
                        maxLength={LINK_MAX_LENGTH}
                        placeholder={t("page.enterUrl")}
                        onValueChange={(value) => {
                            setLink(value);
                            clearError("link");
                        }}
                        error={errors.link}
                    />

                    <FormActions
                        cancelLabel={t("page.cancel")}
                        confirmLabel={t("page.addSponsor")}
                        busy={isBusy}
                        onCancel={closeForm}
                        onConfirm={addSponsor}
                    />
                </Card>
            )}
        </section>
    );
};

export default SponsorshipsSection;
